import { useMutation, useQueryClient } from '@tanstack/react-query';

import { partitionCurrentHeatmapApprovals } from '~/pages/LERE/HeatmapSurcharge/approval';

import HeatmapSurchargeService from '~/services/HeatmapSurchargeService';

import { HeatmapSurchargeApproval } from '~/types/heatmapSurcharge';

import { heatmapSurchargeQueryKeys } from './heatmapSurchargeQueryKeys';

type BulkApproval = {
  approvals: Array<HeatmapSurchargeApproval>;
  note?: string;
};

export function useApproveHeatmapSurchargesBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ approvals, note }: BulkApproval) => {
      const firstApproval = approvals[0];
      if (!firstApproval) {
        throw new Error('Select at least one room night to approve.');
      }
      if (
        approvals.some(
          (approval) =>
            approval.offerId !== firstApproval.offerId ||
            approval.roomTypeId !== firstApproval.roomTypeId ||
            approval.checkInMonth !== firstApproval.checkInMonth,
        )
      ) {
        throw new Error('Bulk approvals must belong to the same offer, room type and check-in month.');
      }

      const filters = { roomTypeId: firstApproval.roomTypeId, checkInMonth: firstApproval.checkInMonth };
      const latest = await queryClient.fetchQuery({
        queryKey: heatmapSurchargeQueryKeys.calendar(firstApproval.offerId, filters),
        queryFn: ({ signal }) => HeatmapSurchargeService.getCalendar(firstApproval.offerId, filters, { signal }),
        staleTime: 0,
      });
      const { currentApprovals, skippedResults } = partitionCurrentHeatmapApprovals(approvals, latest);
      const response = currentApprovals.length
        ? await HeatmapSurchargeService.approveBulk(
            firstApproval.offerId,
            firstApproval.roomTypeId,
            currentApprovals.map((approval) => approval.quote.travelDate),
            note,
          )
        : { results: [], submittedCount: 0, skippedCount: 0, failedCount: 0 };
      const inputOrder = new Map(approvals.map((approval, index) => [approval.quote.travelDate, index]));
      return {
        results: response.results
          .concat(skippedResults)
          .sort(
            (left, right) =>
              (inputOrder.get(left.travelDate) ?? Number.MAX_SAFE_INTEGER) -
              (inputOrder.get(right.travelDate) ?? Number.MAX_SAFE_INTEGER),
          ),
        submittedCount: response.submittedCount,
        skippedCount: response.skippedCount + skippedResults.length,
        failedCount: response.failedCount,
      };
    },
    retry: false,
    onSettled: () => queryClient.invalidateQueries({ queryKey: heatmapSurchargeQueryKeys.calendars() }),
  });
}
